import React from "react";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, LabelList, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Text } from "recharts";
import { Settings, Brain, Star, BarChart3, Activity } from "lucide-react";
import { useTranslation } from "react-i18next";

interface MatchProfilesTabProps {
	profilesData: any;
	candidateProfilesData?: any;
	jobProfilesData?: any;
}

// Component for showing missing data message
const MissingDataMessage = ({ sectionName }: { sectionName: string }) => {
	const { t } = useTranslation();
	
	return (
		<div className="flex flex-col items-center justify-center p-12 text-center bg-gradient-to-br from-slate-50 to-slate-100 rounded-sm border border-slate-200">
			<div className="p-4 rounded-sm bg-slate-200 mb-6">
				<Settings className="h-8 w-8 text-slate-500" />
			</div>
			<h3 className="text-xl text-slate-800 dark:text-gray-200 mb-3">{t('matching.profiles.analysisPending')}</h3>
			<p className="text-sm text-slate-600 dark:text-gray-400 max-w-md leading-relaxed mb-4">
				{t('matching.profiles.analysisIncomplete', { sectionName })}
			</p>
		</div>
	);
};

// Helper function to check if data exists and has meaningful content
const hasValidData = (data: any): boolean => {
	if (!data || typeof data !== 'object') return false;
	
	// Check if it's an empty object
	if (Object.keys(data).length === 0) return false;
	
	// Check if all values are null, undefined, or empty
	const values = Object.values(data);
	return values.some(value => {
		if (value === null || value === undefined) return false;
		if (typeof value === 'string' && value.trim() === '') return false;
		if (typeof value === 'object' && Object.keys(value).length === 0) return false;
		if (Array.isArray(value) && value.length === 0) return false;
		return true;
	});
};

const traitColors = {
	candidate: "#2563eb", // Blue for candidate
	job: "#f59e42",       // Orange for job
	neutral: "#64748b"    // Gray for neutral elements
};

// DISC standard colors: Dominance (Red), Influence (Yellow), Steadiness (Green), Conscientiousness (Blue)
const discColors: Record<string, string> = {
	"Dominance": "#dc2626",
	"Influence": "#eab308",
	"Steadiness": "#16a34a",
	"Conscientiousness": "#2563eb"
};

// MBTI dimension colors for visual distinction
const mbtiDimensionColors: Record<string, { bg: string; text: string; border: string }> = {
	"Energy": { bg: "bg-blue-50", text: "text-blue-700", border: "border-blue-200" },
	"Information": { bg: "bg-green-50", text: "text-green-700", border: "border-green-200" },
	"Decisions": { bg: "bg-rose-50", text: "text-rose-700", border: "border-rose-200" },
	"Lifestyle": { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200" },
};

const SectionCard = ({ title, description, icon, children }: { 
	title: string; 
	description?: string;
	icon?: React.ReactNode;
	children: React.ReactNode;
}) => (
	<Card className="mb-8 shadow-sm border border-slate-200 dark:border-slate-700 bg-white dark:bg-gray-800">
		<CardHeader className="pb-4">
			<div className="flex items-center gap-3">
				{icon && (
					<div className="p-2 rounded-sm bg-blue-50 text-blue-500">
						{icon}
					</div>
				)}
				<div>
					<CardTitle className="text-xl text-slate-800 dark:text-gray-200">{title}</CardTitle>
					{description && (
						<CardDescription className="text-sm text-slate-600 dark:text-gray-400 mt-1">
							{description}
						</CardDescription>
					)}
				</div>
			</div>
		</CardHeader>
		<CardContent className="pt-0">{children}</CardContent>
	</Card>
);

const Explanation = ({ text }: { text: string }) => (
  <div className="text-sm text-gray-700 dark:text-gray-200 mt-2 mb-4 whitespace-pre-line">{text}</div>
);

// Custom label for radar chart points
const RadarValueLabel = (props: any) => {
  const { cx, cy, payload, value, index, color } = props;
  if (value == null) return null;
  // Calculate position for label
  const RADIAN = Math.PI / 180;
  const angle = (360 / 5) * index - 90;
  const radius = props.radius + 18; // offset from axis
  const x = cx + radius * Math.cos(-angle * RADIAN);
  const y = cy + radius * Math.sin(-angle * RADIAN);
  return (
    <Text x={x} y={y} textAnchor="middle" fontSize={12} fill={color} fontWeight={600}>
      {`${Math.round(value * 100)}%`}
    </Text>
  );
};

export const MatchProfilesTab: React.FC<MatchProfilesTabProps> = ({ profilesData, candidateProfilesData, jobProfilesData }) => {
	const { t } = useTranslation();
	// Check if we have any valid profile data
	if (!profilesData || !hasValidData(profilesData)) {
		return (
			<div className="space-y-6">
				<MissingDataMessage sectionName={t('matching.profiles.title')} />
			</div>
		);
	}

	// Safely extract data with fallbacks
	const match_report = profilesData.match_report || {};
	const overall_score = profilesData.match_report.overall_score;
	const summary = profilesData.match_report.summary || t('matching.profiles.noSummaryAvailable');

	// Ensure candidateProfilesData and jobProfilesData are valid
	const safeCandidateData = candidateProfilesData && hasValidData(candidateProfilesData) ? candidateProfilesData : {};
	const safeJobData = jobProfilesData && hasValidData(jobProfilesData) ? jobProfilesData : {};	// DISC
	const discTraits = ["Dominance", "Influence", "Steadiness", "Conscientiousness"];
	const discData = discTraits.map((trait) => ({
		trait,
		score: match_report.DISC?.[trait]?.score ?? null,
		explanation: match_report.DISC?.[trait]?.explanation ?? "",
		candidate: safeCandidateData.DISC?.[trait]?.level === "High" ? 1 : safeCandidateData.DISC?.[trait]?.level === "Moderate" ? 0.6 : 0.3,
		job: safeJobData.DISC?.[trait]?.level === "High" ? 1 : safeJobData.DISC?.[trait]?.level === "Moderate" ? 0.6 : 0.3,
	}));

	// Big Five
	const bigFiveTraits = ["Openness", "Conscientiousness", "Extraversion", "Agreeableness", "Neuroticism"];
	const bigFiveData = bigFiveTraits.map((trait, i) => {
		// Candidate
		let candScore = 0.5;
		const candVal = safeCandidateData.BigFive?.[trait]?.score || "";
		if (typeof candVal === "string") {
			if (candVal.includes("High")) candScore = 0.9;
			else if (candVal.includes("Moderate")) candScore = 0.6;
			else if (candVal.includes("Low")) candScore = 0.3;
		}
		// Job
		let jobScore = 0.5;
		const jobVal = safeJobData.BigFive?.[trait]?.score || "";
		if (typeof jobVal === "string") {
			if (jobVal.includes("High")) jobScore = 0.9;
			else if (jobVal.includes("Moderate")) jobScore = 0.6;
			else if (jobVal.includes("Low")) jobScore = 0.3;
		}		return {
			trait,
			score: match_report.BigFive?.[trait]?.score ?? null,
			explanation: match_report.BigFive?.[trait]?.explanation ?? "",
			candidate: candScore,
			job: jobScore,
		};
	});
	// Prepare radar chart data for OCEAN
	const radarData = bigFiveTraits.map(trait => {
		// Candidate
		let candScore = 0.5;
		const candVal = safeCandidateData.BigFive?.[trait]?.score || "";
		if (typeof candVal === "string") {
			if (candVal.includes("High")) candScore = 0.9;
			else if (candVal.includes("Moderate")) candScore = 0.6;
			else if (candVal.includes("Low")) candScore = 0.3;
		}
		// Job
		let jobScore = 0.5;
		const jobVal = safeJobData.BigFive?.[trait]?.score || "";
		if (typeof jobVal === "string") {
			if (jobVal.includes("High")) jobScore = 0.9;
			else if (jobVal.includes("Moderate")) jobScore = 0.6;
			else if (jobVal.includes("Low")) jobScore = 0.3;
		}
		return {
			trait,
			Candidate: candScore,
			Job: jobScore
		};
	});

	// MBTI
	const mbti = match_report.MBTI || {};
	const hasMbtiData = hasValidData(mbti);

	// Custom Y-axis tick for DISC with color indicators
	const DiscYAxisTick = (props: any) => {
		const { x, y, payload } = props;
		const traitColor = discColors[payload.value as string] || "#64748b";
		return (
			<g transform={`translate(${x},${y})`}>
				<circle cx={-10} cy={0} r={4} fill={traitColor} />
				<text x={-20} y={0} dy={4} textAnchor="end" fontSize={12} fill="#334155">
					{payload.value}
				</text>
			</g>
		);
	};

	// Helper for grouped bar chart (DISC)
	const renderDiscGroupedBarChart = (data: any[]) => (
		<ResponsiveContainer width="100%" height={280}>
			<BarChart
				data={data}
				layout="vertical"
				margin={{ left: 40, right: 48 }}
				barCategoryGap="30%"
				barGap={2}
			>
				<XAxis type="number" domain={[0, 1]} tickFormatter={v => `${Math.round(v * 100)}%`} axisLine={false} tickLine={false} />
				<YAxis type="category" dataKey="trait" width={120} axisLine={false} tickLine={false} tick={<DiscYAxisTick />} />
				<Tooltip 
					formatter={(v: number, name: string) => [`${Math.round(v * 100)}%`, name]}
					contentStyle={{ 
						backgroundColor: 'white', 
						border: '1px solid #e2e8f0',
						borderRadius: '8px',
						boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
					}}
				/>
				<Legend 
					wrapperStyle={{ paddingTop: '10px' }}
					iconType="rect"
				/>				<Bar 
					dataKey="candidate" 
					fill={traitColors.candidate} 
					name={t('matching.profiles.candidate')} 
					isAnimationActive={false}
					radius={[0, 2, 2, 0]}
				>
					<LabelList 
						dataKey="candidate" 
						position="insideRight" 
						formatter={(v: number) => v != null ? `${Math.round(v * 100)}%` : ''} 
						fontSize={11} 
						fill="white"
						fontWeight="600"
					/>
				</Bar>
				<Bar 
					dataKey="job" 
					fill={traitColors.job} 
					name={t('matching.profiles.jobRequirements')} 
					isAnimationActive={false}
					radius={[0, 2, 2, 0]}
				>
					<LabelList 
						dataKey="job" 
						position="insideRight" 
						formatter={(v: number) => v != null ? `${Math.round(v * 100)}%` : ''} 
						fontSize={11} 
						fill="white"
						fontWeight="600"
					/>
				</Bar>
			</BarChart>
		</ResponsiveContainer>
	);	// Helper for radar chart (OCEAN)
	const renderOceanRadarChart = (data: any[]) => (
		<ResponsiveContainer width="100%" height={320}>
			<RadarChart cx="50%" cy="50%" outerRadius="80%" data={data}>
				<PolarGrid gridType="polygon" />
				<PolarAngleAxis dataKey="trait" tick={{ fontSize: 12 }} />
				<PolarRadiusAxis 
					angle={30} 
					domain={[0, 1]} 
					tickFormatter={v => `${Math.round(v * 100)}%`}
					tick={false}
					axisLine={false}
				/>				<Radar 
					name={t('matching.profiles.candidate')} 
					dataKey="Candidate" 
					stroke={traitColors.candidate} 
					fill={traitColors.candidate} 
					fillOpacity={0.3}
					strokeWidth={2}
					dot={{ fill: traitColors.candidate, strokeWidth: 2, r: 4 }}
					isAnimationActive={false}
					label={(props: any) => props.value != null ? <RadarValueLabel {...props} color={traitColors.candidate} /> : null}
				/>
				<Radar 
					name={t('matching.profiles.jobRequirements')} 
					dataKey="Job" 
					stroke={traitColors.job} 
					fill={traitColors.job} 
					fillOpacity={0.3}
					strokeWidth={2}
					dot={{ fill: traitColors.job, strokeWidth: 2, r: 4 }}
					isAnimationActive={false}
					label={(props: any) => props.value != null ? <RadarValueLabel {...props} color={traitColors.job} /> : null}
				/>
				<Legend 
					wrapperStyle={{ paddingTop: '10px' }}
					iconType="rect"
				/>
				<Tooltip 
					formatter={(v: number, name: string) => [`${Math.round(v * 100)}%`, name]}
					contentStyle={{ 
						backgroundColor: 'white', 
						border: '1px solid #e2e8f0',
						borderRadius: '8px',
						boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
					}}
				/>
			</RadarChart>
		</ResponsiveContainer>
	);
	return (
		<div className="space-y-8 max-w-7xl mx-auto">			{/* Analysis Summary Header */}
			<div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-sm p-6 border border-blue-200">
				<div className="flex items-center gap-4">
					<div className="p-3 bg-blue-100 rounded-sm">
						<Brain className="h-8 w-8 text-blue-500" />
					</div>
					<div className="flex-1">
						<h2 className="text-xl text-slate-800 dark:text-gray-200 mb-2">{t('matching.profiles.title')}</h2>
						<p className="text-slate-600 dark:text-gray-400">
							{t('matching.profiles.description')}
						</p>
					</div>
					{typeof overall_score === 'number' && (
						<div className="text-right">
							<Badge variant="secondary" className="mb-2">
								<Star className="h-3 w-3 mr-1" />
								{`${Math.round(overall_score * 100)}%`}
							</Badge>
							<p className="text-xs text-slate-500 dark:text-gray-400">{t('matching.profiles.matchScore')}</p>
						</div>
					)}
				</div>
			</div>			{/* Overall Score & Summary */}
			<SectionCard 
				title={t('matching.profiles.overallMatch')} 
				description={t('matching.profiles.overallMatchDescription')}
				icon={<Star className="h-5 w-5" />}
			>
				<div className="flex items-center gap-6 mb-2">
					<div className="h-20 w-20 bg-blue-500 text-white rounded-sm flex items-center justify-center text-3xl font-bold">
						{typeof overall_score === 'number' ? `${Math.round(overall_score * 100)}%` : ''}
					</div>
					<div className="flex-1 text-gray-700 dark:text-gray-200 text-base">{summary}</div>
				</div>
			</SectionCard>

			{/* DISC and Big Five (OCEAN) side by side */}
			<div className="grid grid-cols-1 lg:grid-cols-2 gap-8">				{/* DISC Profile */}
				<SectionCard 
					title={t('matching.profiles.discComparison')} 
					description={t('matching.profiles.discDescription')}
					icon={<BarChart3 className="h-5 w-5" />}
				>					{hasValidData(match_report.DISC) ? (
						<div className="space-y-4">
							{/* DISC Trait Colors Legend */}
							<div className="grid grid-cols-2 gap-2 mb-3 p-3 bg-slate-50 rounded-sm border border-slate-200">
								{Object.entries(discColors).map(([trait, color]) => (
									<div key={trait} className="flex items-center gap-2">
										<div className="w-3 h-3 rounded-full" style={{ backgroundColor: color }}></div>
										<span className="text-xs font-medium text-slate-600 dark:text-gray-400">{trait}</span>
									</div>
								))}
							</div>
							{/* Candidate vs Job Legend */}
							<div className="flex items-center justify-center gap-6 mb-4 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-sm border">
								<div className="flex items-center gap-2">
									<div className="w-4 h-4 rounded" style={{ backgroundColor: traitColors.candidate }}></div>
									<span className="text-sm font-medium text-gray-700 dark:text-gray-200">{t('matching.profiles.candidate')}</span>
								</div>
								<div className="flex items-center gap-2">
									<div className="w-4 h-4 rounded" style={{ backgroundColor: traitColors.job }}></div>
									<span className="text-sm font-medium text-gray-700 dark:text-gray-200">{t('matching.profiles.jobRequirements')}</span>
								</div>
							</div>
							{renderDiscGroupedBarChart(discData)}
							{discData.map(d => d.explanation && (
								<div key={d.trait} className="text-sm text-gray-700 dark:text-gray-200 mt-2 mb-4 whitespace-pre-line">
									<strong>{d.trait}:</strong> {d.explanation}
								</div>
							))}						</div>
					) : (
						<MissingDataMessage sectionName={t('matching.profiles.discAnalysis')} />
					)}
				</SectionCard>				{/* Big Five Profile */}
				<SectionCard 
					title={t('matching.profiles.bigFiveComparison')} 
					description={t('matching.profiles.bigFiveDescription')}
					icon={<Activity className="h-5 w-5" />}
				>					{hasValidData(match_report.BigFive) ? (
						<div className="space-y-4">
							{/* Color Legend */}
							<div className="flex items-center justify-center gap-6 mb-4 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-sm border">
								<div className="flex items-center gap-2">
									<div className="w-4 h-4 rounded" style={{ backgroundColor: traitColors.candidate }}></div>
									<span className="text-sm font-medium text-gray-700 dark:text-gray-200">{t('matching.profiles.candidate')}</span>
								</div>
								<div className="flex items-center gap-2">
									<div className="w-4 h-4 rounded" style={{ backgroundColor: traitColors.job }}></div>
									<span className="text-sm font-medium text-gray-700 dark:text-gray-200">{t('matching.profiles.jobRequirements')}</span>
								</div>
							</div>
							{renderOceanRadarChart(radarData)}
							{bigFiveData.map(d => d.explanation && (
								<div key={d.trait} className="text-sm text-gray-700 dark:text-gray-200 mt-2 mb-4 whitespace-pre-line">
									<strong>{d.trait}:</strong> {d.explanation}
								</div>
							))}						</div>
					) : (
						<MissingDataMessage sectionName={t('matching.profiles.bigFiveAnalysis')} />
					)}
				</SectionCard>
			</div>

			{/* MBTI Comparison */}
			<SectionCard 
				title={t('matching.profiles.mbtiComparison')} 
				description={t('matching.profiles.mbtiDescription')}
				icon={<Brain className="h-5 w-5" />}
			>				{hasMbtiData ? (
					<div className="space-y-4">
						<div className="mb-2 text-sm text-gray-700 dark:text-gray-200">
							<b>{t('matching.profiles.typeMatchScore')}</b> {typeof mbti.type_match_score === 'number' ? `${Math.round(mbti.type_match_score * 100)}%` : t('matching.profiles.notAvailable')}
						</div>
						{mbti.explanation && (
							<div className="text-sm text-gray-700 dark:text-gray-200 mt-2 mb-4 whitespace-pre-line">
								{mbti.explanation}
							</div>
						)}
					</div>
				) : (
					<MissingDataMessage sectionName={t('matching.profiles.mbtiAnalysis')} />
				)}
			</SectionCard>
		</div>
	);
};